import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  Routes,
  Route,
} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Components
import CategoryCard from '../../../components/CategoryCard';
import Loading from '../../../components/global/loading/Loading';

// Hooks & Context
import { useCategory } from '../../../context/CategoryContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { Category } from '../../../models/Category';
import AddCategory from './AddCategory';
import EditCategory from './EditCategory';

// Niestandardowy komponent przycisku
const CustomButton = ({
  to,
  onClick,
  className = '',
  variant = 'primary',
  disabled = false,
  children,
}: {
  to?: string;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  children: React.ReactNode;
}) => {
  // Wspólne style bazowe
  const baseStyles = `
    font-medium text-sm rounded-md transition-all duration-200
    flex items-center justify-center gap-2 whitespace-nowrap
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `;

  // Style dla różnych wariantów
  const variantStyles = {
    primary: `bg-primary hover:bg-primary-dark text-white py-2 px-4
              shadow-sm hover:shadow hover:translate-y-[-1px] active:translate-y-[0px]`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4`,
    outline: `border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 px-4`,
  };

  // Łączymy wszystkie style
  const buttonStyles = `${baseStyles} ${variantStyles[variant]}`;

  // Jeśli mamy atrybut "to", renderujemy Link, w przeciwnym razie button
  if (to) {
    return (
      <Link to={to} className={buttonStyles}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} className={buttonStyles}>
      {children}
    </button>
  );
};

function Categories() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { control } = useForm();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryAncestors, setCategoryAncestors] = useState<Category[]>([]);
  const [totalCategories, setTotalCategories] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usingSearch, setUsingSearch] = useState<boolean>(false);

  const { getCategories, searchCategories } = useCategory();
  const debouncedQuery = useDebounce(query, 500);

  const getSearchCategoriesData = useCallback(
    async (page: number, query: string) => {
      setIsLoading(true);
      try {
        const response = await searchCategories(page, 10, query);
        setCategories(response.categories);
        setTotalCategories(response.totalCategories);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
        setUsingSearch(true);
      } finally {
        setIsLoading(false);
      }
    },
    [searchCategories],
  );

  const getCategoriesData = useCallback(
    async (id?: number) => {
      setIsLoading(true);
      try {
        const response = await getCategories(id);
        setCategories(response.categories);
        setCategoryAncestors(response.ancestors);
        setUsingSearch(false);
      } finally {
        setIsLoading(false);
      }
    },
    [getCategories],
  );

  // Upewnij się, że ID jest numerem, jeśli jest dostępne
  useEffect(() => {
    if (id && !isNaN(Number(id))) {
      getCategoriesData(Number(id));
    } else if (id === undefined) {
      getCategoriesData();
    }
  }, [id, getCategoriesData]);

  // Efekt dla wyszukiwania
  useEffect(() => {
    if (debouncedQuery !== '') {
      getSearchCategoriesData(currentPage, debouncedQuery);
    }
  }, [debouncedQuery, currentPage, getSearchCategoriesData]);

  const handleClearSearch = () => {
    setQuery('');
    getCategoriesData(id ? Number(id) : undefined);
  };

  const handleCategoryClick = (categoryId: number) => {
    setQuery(''); // Czyścimy wyszukiwanie przy nawigacji
    navigate(`/admin/kategorie/${categoryId}`);
  };

  const handleBreadcrumbClick = (categoryId?: number) => {
    setQuery(''); // Czyścimy wyszukiwanie przy nawigacji po okruszkach
    if (categoryId) {
      navigate(`/admin/kategorie/${categoryId}`);
    } else {
      navigate('/admin/kategorie');
    }
  };

  // Sprawdź czy aktualnie jesteśmy na stronie dodawania lub edycji
  const isAddingCategory = location.pathname.includes('/dodaj');
  const isEditingCategory = location.pathname.includes('/edytuj');

  // Jeśli jesteśmy na stronie dodawania lub edycji, renderuj odpowiedni komponent
  if (isAddingCategory) {
    return <AddCategory id={id || ''} />;
  }

  if (isEditingCategory) {
    return <EditCategory id={id || ''} />;
  }

  // W przeciwnym razie renderuj listę kategorii
  return (
    <div className="flex flex-col items-stretch space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CategoryIcon className="text-primary" />
            Kategorie produktów
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Zarządzaj strukturą kategorii i podkategorii sklepu
          </p>
        </motion.div>

        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="self-end sm:self-auto flex gap-2"
        >
          <CustomButton
            to={`dodaj`}
            variant="primary"
          >
            <AddIcon fontSize="small" />
            <span>{id ? 'Dodaj podkategorię' : 'Dodaj kategorię główną'}</span>
          </CustomButton>
        </motion.div>
      </div>

      {/* Search and navigation */}
      <motion.div
        className="grid grid-cols-1 gap-4"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative">
          <Controller
            name="search"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  {...field}
                  placeholder="Szukaj kategorii (nazwa, opis)"
                  className="pl-10 pr-10 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <ClearIcon
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
                    fontSize="small"
                    onClick={handleClearSearch}
                  />
                )}
              </div>
            )}
          />
        </div>

        {/* Breadcrumb navigation */}
        {!usingSearch && (
          <motion.nav
            className="flex"
            aria-label="Breadcrumb"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <ol className="flex items-center flex-wrap">
              <li className="flex items-center">
                <CustomButton
                  onClick={() => handleBreadcrumbClick()}
                  variant="secondary"
                  className="py-1.5 px-3"
                >
                  <HomeIcon fontSize="small" />
                  <span>Kategorie główne</span>
                </CustomButton>
              </li>

              {categoryAncestors.map((category, index) => (
                <li key={category.id} className="flex items-center">
                  <NavigateNextIcon
                    className="text-gray-500 mx-1"
                    fontSize="small"
                  />
                  <CustomButton
                    onClick={() => handleBreadcrumbClick(category.id)}
                    variant={
                      index === categoryAncestors.length - 1
                        ? 'primary'
                        : 'secondary'
                    }
                    className="py-1.5"
                  >
                    {category.name}
                  </CustomButton>
                </li>
              ))}
            </ol>
          </motion.nav>
        )}
      </motion.div>

      {/* Status bar */}
      <motion.div
        className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {usingSearch ? (
          <div>
            Wyświetlono{' '}
            <span className="font-semibold">{categories.length}</span> z{' '}
            <span className="font-semibold">{totalCategories}</span> kategorii
            spełniających kryteria
          </div>
        ) : (
          <div>
            {categoryAncestors.length > 0 ? (
              <div className="flex items-center">
                <span>Kategoria:</span>
                <span className="ml-1 font-semibold text-primary">
                  {categoryAncestors[categoryAncestors.length - 1]?.name}
                </span>
              </div>
            ) : (
              <span>Wyświetlanie kategorii głównych</span>
            )}
          </div>
        )}

        {usingSearch && totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span>
              Strona {currentPage} z {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() =>
                  currentPage > 1 &&
                  getSearchCategoriesData(currentPage - 1, query)
                }
                disabled={currentPage <= 1}
                className={`p-1 rounded ${
                  currentPage <= 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon
                  style={{
                    fontSize: '0.9rem',
                    transform: 'rotate(180deg)',
                  }}
                />
              </button>
              <button
                onClick={() =>
                  currentPage < totalPages &&
                  getSearchCategoriesData(currentPage + 1, query)
                }
                disabled={currentPage >= totalPages}
                className={`p-1 rounded ${
                  currentPage >= totalPages
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ArrowForwardIosIcon style={{ fontSize: '0.9rem' }} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="loading"
            className="flex justify-center py-12"
          >
            <Loading />
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="content"
            transition={{ duration: 0.3 }}
          >
            {categories.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                <AnimatePresence>
                  {categories.map((category, index) => (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                      }}
                      className="relative"
                    >
                      <CategoryCard
                        key={category.id}
                        getCategoriesData={getCategoriesData}
                        category={category}
                        showParent={usingSearch}
                        onCategoryClick={handleCategoryClick}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <CategoryIcon
                  style={{ fontSize: '3rem' }}
                  className="text-gray-300 mb-2"
                />
                <h3 className="text-lg font-medium text-gray-700">
                  {usingSearch
                    ? 'Nie znaleziono pasujących kategorii'
                    : id
                      ? 'Ta kategoria nie zawiera podkategorii'
                      : 'Brak kategorii głównych'}
                </h3>
                <p className="text-gray-500 mt-1">
                  {usingSearch
                    ? 'Spróbuj użyć innych słów kluczowych w wyszukiwaniu'
                    : id
                      ? 'Możesz dodać podkategorie klikając przycisk poniżej'
                      : 'Dodaj pierwszą kategorię główną, aby rozpocząć budowę struktury'}
                </p>
                <CustomButton to="dodaj" className="mt-4 py-2.5 px-5">
                  <AddIcon fontSize="small" />
                  <span>
                    {id ? 'Dodaj podkategorię' : 'Dodaj kategorię główną'}
                  </span>
                </CustomButton>
              </div>
            )}

            {/* Paginacja dla wyników wyszukiwania */}
            {usingSearch && totalPages > 1 && (
              <div className="flex justify-center py-6">
                <div className="flex items-center gap-1">
                  <CustomButton
                    onClick={() => getSearchCategoriesData(1, query)}
                    disabled={currentPage === 1}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === 1 ? 'opacity-50' : ''
                    }`}
                  >
                    Pierwsza
                  </CustomButton>

                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={idx}
                        onClick={() => getSearchCategoriesData(pageNum, query)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                        ${
                          currentPage === pageNum
                            ? 'bg-primary text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <CustomButton
                    onClick={() => getSearchCategoriesData(totalPages, query)}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    className={`px-3 py-1.5 text-xs ${
                      currentPage === totalPages ? 'opacity-50' : ''
                    }`}
                  >
                    Ostatnia
                  </CustomButton>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Główny komponent routingu
function CategoriesRouter() {
  return (
    <Routes>
      <Route path="dodaj" element={<AddCategory id="" />} />
      <Route path=":id/dodaj" element={<Categories />} />
      <Route path=":id/edytuj" element={<Categories />} />
      <Route path=":id" element={<Categories />} />
      <Route path="" element={<Categories />} />
    </Routes>
  );
}

export default CategoriesRouter;
